import React from 'react';
import clsx from 'clsx';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { CitationMarkdownAnchor } from '@oysterjs/ais/components/workspaces/citation-hover-card';
import { rehypeFootnoteCitations } from '@oysterjs/ais/lib/footnote-citations';
import { markdownSanitizeSchema } from '@oysterjs/ais/lib/markdownSanitizeSchema';

const REMARK_PLUGINS: React.ComponentProps<typeof Markdown>['remarkPlugins'] = [[remarkGfm, {}]];

type RehypePlugins = NonNullable<React.ComponentProps<typeof Markdown>['rehypePlugins']>;

const REHYPE_PLUGINS: RehypePlugins = [
  [rehypeRaw, { tagFilter: true }],
  [rehypeSanitize, markdownSanitizeSchema]
];

// Citation resolution runs after sanitize so ids already carry the user-content- clobber
// prefix and the target/rel it sets are not stripped.
const CITATION_REHYPE_PLUGINS: RehypePlugins = [...REHYPE_PLUGINS, rehypeFootnoteCitations];

// Don't split text inside these — they should render verbatim (whitespace matters
// for code, and the streaming-word spans would interfere with monospace layout).
const ANIMATE_OPT_OUT_TAGS = new Set(['code', 'pre']);

// HTML void elements cannot receive children; passing children into cloneElement
// would trip React's "void element must neither have children …" warning, so we
// hand these back to react-markdown unchanged. (Task-list checkboxes from
// remark-gfm are a common source of `<input>` showing up inside `<li>`.)
const VOID_ELEMENT_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'source',
  'track',
  'wbr'
]);

// Split text on whitespace while preserving the whitespace runs as plain text so
// the browser can still break lines between words naturally.
const WORD_TOKEN_RE = /(\s+)/;

/**
 * Animation gate carried via context so the recursive splitter can ask "has the
 * Nth word (in document order) already been shown in a previous commit?".
 *
 * We deliberately use a *cumulative* per-render counter rather than reuse the
 * tree-position key: when the streaming parser flips a span from literal text
 * (e.g. "**bo") into a real element (`<strong>bold</strong>`), the surrounding
 * tree position changes but the word's index in document order does not. Gating
 * by document-order index keeps already-shown words from re-fading when the AST
 * reshapes around them.
 */
type AnimationGate = {
  next: () => number;
  isAlreadySeen: (index: number) => boolean;
};

const NULL_GATE: AnimationGate = {
  next: () => -1,
  // When animation is disabled, treat every word as already-seen so the
  // streaming-word class is never applied.
  isAlreadySeen: () => true
};

const AnimationGateContext = React.createContext<AnimationGate>(NULL_GATE);

/**
 * Renders a single streaming word.
 *
 * The animate decision is captured into a ref at *first render* and never
 * re-read. That matters because the underlying CSS keyframe runs exactly once,
 * when the `<span>` first mounts; if we let the `className` toggle between
 * `'streaming-word'` and `undefined` on subsequent renders, the browser would
 * cancel the in-flight animation and snap the element to its base style — which
 * is exactly the "flashing back and forth" symptom.
 *
 * Custom memo equality intentionally ignores the `animate` prop: the gate's
 * answer changes from `true` → `false` on the second render of a freshly-shown
 * word, but the snapshot has already made the decision and the DOM doesn't
 * care, so skipping that re-render is safe and saves work.
 */
const StreamingWord = React.memo(
  ({ text, animate }: { text: string; animate: boolean }) => {
    const animateOnMountRef = React.useRef(animate);
    return <span className={animateOnMountRef.current ? 'streaming-word' : undefined}>{text}</span>;
  },
  (prev, next) => prev.text === next.text
);
StreamingWord.displayName = 'StreamingWord';

const splitTextIntoWords = (
  text: string,
  keyPrefix: string,
  gate: AnimationGate
): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  const parts = text.split(WORD_TOKEN_RE);
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part) continue;
    if (/^\s+$/.test(part)) {
      out.push(part);
      continue;
    }
    const wordIdx = gate.next();
    const animate = !gate.isAlreadySeen(wordIdx);
    out.push(<StreamingWord key={`${keyPrefix}:${i}`} text={part} animate={animate} />);
  }
  return out;
};

const splitChildrenIntoWords = (
  children: React.ReactNode,
  keyPrefix: string,
  gate: AnimationGate
): React.ReactNode => {
  const arr = React.Children.toArray(children);
  return arr.map((child, idx) => {
    const childKey = `${keyPrefix}.${idx}`;
    if (typeof child === 'string') {
      return (
        <React.Fragment key={childKey}>{splitTextIntoWords(child, childKey, gate)}</React.Fragment>
      );
    }
    if (React.isValidElement(child)) {
      const tag = typeof child.type === 'string' ? child.type : null;
      if (tag && (ANIMATE_OPT_OUT_TAGS.has(tag) || VOID_ELEMENT_TAGS.has(tag))) {
        return child;
      }
      const nested = splitChildrenIntoWords(child.props.children, childKey, gate);
      // Use the position-based childKey unconditionally — `React.Children.toArray`
      // re-keys items with `.$N` prefixes that can shift when the children array
      // shape changes between streaming renders, which would otherwise force
      // React to unmount/remount stable subtrees.
      return React.cloneElement(child, { key: childKey }, nested);
    }
    return child;
  });
};

// react-markdown passes a hast `node` prop into custom components that should
// not be forwarded to the underlying DOM element.
const buildAnimatedBlock = (Tag: keyof JSX.IntrinsicElements) => {
  const Component = ({ node: _node, children, ...rest }: any) => {
    const gate = React.useContext(AnimationGateContext);
    return React.createElement(Tag, rest, splitChildrenIntoWords(children, Tag, gate));
  };
  Component.displayName = `AnimatedMarkdown(${Tag})`;
  return Component;
};

const ANIMATED_COMPONENTS: Components = {
  p: buildAnimatedBlock('p'),
  li: buildAnimatedBlock('li'),
  h1: buildAnimatedBlock('h1'),
  h2: buildAnimatedBlock('h2'),
  h3: buildAnimatedBlock('h3'),
  h4: buildAnimatedBlock('h4'),
  h5: buildAnimatedBlock('h5'),
  h6: buildAnimatedBlock('h6'),
  td: buildAnimatedBlock('td'),
  th: buildAnimatedBlock('th'),
  blockquote: buildAnimatedBlock('blockquote'),
  dd: buildAnimatedBlock('dd'),
  dt: buildAnimatedBlock('dt')
};

/** Citation hover only when resolveCitations is on (resolved http markers exist). */
const CITATION_COMPONENTS: Components = {
  a: CitationMarkdownAnchor
};

const CITATION_ANIMATED_COMPONENTS: Components = {
  ...ANIMATED_COMPONENTS,
  a: CitationMarkdownAnchor
};

type SanitizedMarkdownProps = {
  content: string;
  /**
   * Promote footnote definitions that are bare citation URLs onto their inline markers.
   * Off by default: only the agent response surface renders Cara citations, and leaving it
   * off while a message streams keeps a partial URL from becoming a clickable destination.
   */
  resolveCitations?: boolean;
};

/**
 * Renders user-/agent-authored markdown with the standard sanitize schema and the
 * `<br>` → space normalization that every Cara conversation surface relies on.
 *
 * Use this in place of <Markdown> directly so the plugin set, sanitize schema, and
 * pre-render normalization stay consistent across the app.
 *
 * For streaming surfaces where you want each newly-arrived word to fade in,
 * use {@link AnimatedSanitizedMarkdown} instead.
 *
 * Memoized so finished conversation rows (historical messages, completed stage
 * events) don't re-parse markdown on every subscription tick during a streaming
 * response; content and resolveCitations are the only props, so default shallow
 * equality is exactly right.
 */
export const SanitizedMarkdown = React.memo((props: SanitizedMarkdownProps) => (
  <Markdown
    remarkPlugins={REMARK_PLUGINS}
    rehypePlugins={props.resolveCitations ? CITATION_REHYPE_PLUGINS : REHYPE_PLUGINS}
    components={props.resolveCitations ? CITATION_COMPONENTS : undefined}
  >
    {props.content.replace(/<br>/g, ' ')}
  </Markdown>
));
SanitizedMarkdown.displayName = 'SanitizedMarkdown';

/**
 * Streaming-aware variant of {@link SanitizedMarkdown}. Each word of rendered
 * text is wrapped in a span that fades in on mount, so words appended by
 * subscription updates feather in smoothly instead of popping into place.
 *
 * Words rendered in an earlier commit are *not* re-animated when the parser
 * reshapes the AST around them (e.g. a partial `**bo` becoming a real
 * `<strong>bold</strong>`): the splitter tracks each word's index in document
 * order and the animation decision is snapshotted on the span's first mount so
 * it never toggles mid-flight.
 *
 * The very first render is also treated as "all words already seen", so
 * mounting the component with pre-existing content (e.g. reloading a
 * conversation, scrolling a historical message into view) does not kick off a
 * page-wide fade-in storm. Only words appended in *subsequent* commits — i.e.
 * actively-streaming tokens — animate.
 *
 * Memoized so a parent re-render with the same content string doesn't re-parse
 * markdown or re-walk the word splitter. Memo preserves the component instance,
 * so the internal animation-gate refs survive the bailout unchanged.
 */
const AnimatedSanitizedMarkdownImpl = (props: SanitizedMarkdownProps) => {
  // Cumulative word counter for the in-progress render.
  const wordCounterRef = React.useRef(0);
  // High-water mark of words rendered in the previous *committed* render. Words
  // with index < this value have already been shown, so we suppress the fade.
  const seenWordsRef = React.useRef(0);
  // Tracks whether the component has committed at least once. While false, the
  // gate reports every word as already-seen so the initial mount does not
  // animate pre-existing content. The post-commit effect flips this to true.
  const hasCommittedRef = React.useRef(false);

  // Reset the counter at the start of every render of this component so the
  // recursive splitter can hand out fresh, monotonically increasing indices.
  wordCounterRef.current = 0;

  // Stable context object: mutating refs through closures avoids re-creating
  // the gate every render (which would invalidate custom-component memoization
  // and force the entire markdown subtree to remount).
  const gate = React.useMemo<AnimationGate>(
    () => ({
      next: () => wordCounterRef.current++,
      isAlreadySeen: (index: number) => !hasCommittedRef.current || index < seenWordsRef.current
    }),
    []
  );

  // After each commit, promote the words we just rendered to "seen" so the
  // next streaming chunk only animates the newly-appended tail. Keying on
  // content ensures we don't accidentally bump the watermark on re-renders
  // where the markdown didn't actually re-render.
  React.useEffect(() => {
    seenWordsRef.current = wordCounterRef.current;
    hasCommittedRef.current = true;
  }, [props.content]);

  return (
    <AnimationGateContext.Provider value={gate}>
      <Markdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={props.resolveCitations ? CITATION_REHYPE_PLUGINS : REHYPE_PLUGINS}
        components={props.resolveCitations ? CITATION_ANIMATED_COMPONENTS : ANIMATED_COMPONENTS}
      >
        {props.content.replace(/<br>/g, ' ')}
      </Markdown>
    </AnimationGateContext.Provider>
  );
};

export const AnimatedSanitizedMarkdown = React.memo(AnimatedSanitizedMarkdownImpl);
AnimatedSanitizedMarkdown.displayName = 'AnimatedSanitizedMarkdown';

/** Dark-mode typography shared by MarkdownContainer and MarkdownContainerXS. */
const markdownProseDarkClasses = [
  'dark:prose-invert',
  'dark:prose-p:text-neutral-300',
  'dark:prose-li:text-neutral-300',
  'dark:prose-strong:text-neutral-100',
  'dark:prose-headings:text-neutral-100',
  'dark:prose-a:text-sky-400',
  'prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-code:font-normal prose-code:before:content-none prose-code:after:content-none',
  'dark:prose-code:text-neutral-200',
  'dark:prose-blockquote:text-neutral-400',
  'dark:prose-hr:border-neutral-700'
];

/**
 * Only the parts of a table that have no light-mode color to mirror. The surface,
 * borders and cell text are paired inline against their light values below, so a
 * second copy here would fight them for the same properties.
 */
const markdownTableDarkClasses = ['dark:prose-thead:text-neutral-400'];

/**
 * Row dividers use per-cell borders (DataTable-style). Typography also draws
 * thead/tbody tr borders; drop those so we do not stack lines, and drop the last
 * body row's cell borders so the table does not end on a divider.
 */
const markdownTableBorderClasses = [
  'prose-thead:border-b-0',
  'prose-tr:border-b-0',
  '[&_tbody_tr:last-child_td]:border-b-0'
];

export const MarkdownContainer = (props: { className?: string; children: React.ReactNode }) => (
  <div
    className={clsx(
      // Basic prose style
      'prose prose-slate prose-sky prose-sm w-full max-w-full',
      markdownProseDarkClasses,
      // Override prose table styles to match DataTable
      'prose-table:text-left prose-table:text-sm prose-table:bg-white dark:prose-table:bg-neutral-800 prose-table:shadow prose-table:rounded-md',
      markdownTableDarkClasses,
      markdownTableBorderClasses,
      'prose-th:border-b prose-th:border-neutral-950/10 dark:prose-th:border-white/10 prose-th:px-4 prose-th:py-1.5 prose-th:font-medium prose-th:text-xs prose-th:text-left',
      'prose-td:border-b prose-td:border-neutral-950/5 dark:prose-td:border-white/10 prose-td:px-4 prose-td:py-2.5',
      // Make horizontal rules more compact
      'prose-hr:my-6',
      // Adjust list item sizes to be the similar style to body
      'prose-li:text-base prose-li:leading-relaxed',
      // Adjust header styles
      'prose-h1:font-body prose-h1:font-semibold prose-h1:text-2xl',
      'prose-h2:font-body prose-h2:font-semibold prose-h2:text-xl',
      'prose-h3:font-body prose-h3:font-semibold prose-h3:text-lg',
      'prose-h4:font-body prose-h4:font-medium prose-h4:text-base',
      'prose-h5:font-body prose-h5:font-medium prose-h5:text-sm',
      'prose-h6:font-body prose-h6:font-medium prose-h6:text-sm',
      // Adjust line breaks
      'text-pretty',
      // Adjust code block styles
      'prose-pre:text-sm prose-pre:leading-relaxed prose-pre:text-neutral-700 dark:prose-pre:text-neutral-300 prose-pre:bg-neutral-100 dark:prose-pre:bg-neutral-700 prose-pre:rounded-md prose-pre:border prose-pre:border-neutral-950/10 dark:prose-pre:border-white/10 prose-pre:p-4',

      // Include user-provided overrides
      props.className
    )}
  >
    {props.children}
  </div>
);

export const MarkdownContainerXS = (props: { className?: string; children: React.ReactNode }) => (
  <div
    className={clsx(
      // Basic prose style
      'prose prose-slate prose-sky prose-sm w-full max-w-full',
      markdownProseDarkClasses,
      // Override prose table styles to match DataTable
      'prose-table:text-left prose-table:text-xs prose-table:bg-white dark:prose-table:bg-neutral-800 prose-table:shadow prose-table:rounded-md',
      markdownTableDarkClasses,
      markdownTableBorderClasses,
      'prose-th:border-b prose-th:border-neutral-950/10 dark:prose-th:border-white/10 prose-th:px-4 prose-th:py-1.5 prose-th:font-medium prose-th:text-xs prose-th:text-left',
      'prose-td:border-b prose-td:border-neutral-950/5 dark:prose-td:border-white/10 prose-td:px-4 prose-td:py-2.5',
      // Make horizontal rules more compact
      'prose-hr:my-6',
      // Adjust list item sizes to be the similar style to body
      'prose-li:text-base prose-li:leading-normal',
      // Adjust header styles
      'prose-h1:font-heading prose-h1:font-semibold prose-h1:text-lg',
      'prose-h2:font-heading prose-h2:font-semibold prose-h2:text-md',
      'prose-h3:font-heading prose-h3:font-semibold prose-h3:text-sm',
      'prose-h4:font-body prose-h4:font-medium prose-h4:text-sm',
      'prose-h5:font-body prose-h5:font-medium prose-h5:text-sm',
      'prose-h6:font-body prose-h6:font-medium prose-h6:text-xs',
      // Adjust line breaks
      'text-pretty',
      // Adjust code block styles
      'prose-pre:text-xs prose-pre:leading-normal prose-pre:text-neutral-700 dark:prose-pre:text-neutral-300 prose-pre:bg-neutral-100 dark:prose-pre:bg-neutral-700 prose-pre:rounded-md prose-pre:border prose-pre:border-neutral-950/10 dark:prose-pre:border-white/10 prose-pre:p-4',
      // Smaller text
      'prose-p:text-sm prose-li:text-sm',

      // Include user-provided overrides
      props.className
    )}
  >
    {props.children}
  </div>
);
